import { usePremiumInfoUrl } from '@src/utils/premiumReturn';
import { isDotCom } from '@src/site';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { openCookieSettings } from '@src/utils/cookieConsent';
import { useAppSelector } from '@src/state/hooks';
import { guestOnlyGame } from '@src/state/guestGame';
import { mainSiteUrl, premiumGameUrl } from '@src/routing/boxDomainNavigation';
import { useTranslation } from 'react-i18next';
import { Bar } from '@src/components/Bar/Bar';
import { Brand } from '@src/components/Brand/Brand';
import { Cluster } from '@src/components/Cluster/Cluster';
import { Container, type ContainerWidth } from '@src/components/Container/Container';
import { Link } from '@src/components/Link/Link';
import { LocaleToggle } from '@src/components/LocaleToggle/LocaleToggle';
import { ThemeToggle } from '@src/components/ThemeToggle/ThemeToggle';
import { Stack } from '@src/components/Stack/Stack';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import {
  SECRET_SANTA_FOR_CLASSROOM_PATH,
  SECRET_SANTA_FOR_COWORKERS_PATH,
  SECRET_SANTA_FOR_FAMILY_PATH,
  SECRET_SANTA_FOR_FRIENDS_PATH,
} from '@src/routing/routes';
import { openSupportDialog } from '@src/support/openSupportDialog';
import './Footer.css';

interface FooterColumn {
  titleKey: string;
  links: { href?: string; labelKey: string }[];
}

const COLUMNS: FooterColumn[] = [
  {
    titleKey: 'footer.game',
    links: [
      { href: '/box/new', labelKey: 'footer.createGame' },
      { href: '/randomizer', labelKey: 'footer.quickDraw' },
      { href: '/premium', labelKey: 'footer.premium' },
      // { href: '/gifts', labelKey: 'gifts.title' },
      { href: '/account/boxes', labelKey: 'footer.myGames' },
    ],
  },
  {
    titleKey: 'footer.formats',
    links: [
      { href: SECRET_SANTA_FOR_FRIENDS_PATH, labelKey: 'footer.friends' },
      { href: SECRET_SANTA_FOR_COWORKERS_PATH, labelKey: 'footer.coworkers' },
      { href: SECRET_SANTA_FOR_FAMILY_PATH, labelKey: 'footer.family' },
      { href: SECRET_SANTA_FOR_CLASSROOM_PATH, labelKey: 'footer.classroom' },
    ],
  },
  {
    titleKey: 'footer.help',
    links: [
      { href: '/faq', labelKey: 'footer.faq' },
      { labelKey: 'footer.contact' },
    ],
  },
];

export interface FooterProps {
  width?: ContainerWidth;
  testId?: string;
}

/** Inverse site footer: wordmark, three link columns, legal row. */
export function Footer({ width = 'content', testId = 'footer' }: FooterProps) {
  const premiumInfoHref = usePremiumInfoUrl();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.session.user);
  const onlyGame = guestOnlyGame(user);

  return (
    <footer className="footer" {...testIdAttr(testId)}>
      <Container width={width} gutter="default" pad="page">
        <Stack gap={10}>
          <div className="footer__nav">
            <Stack gap={3}>
              <Brand href="/" tone="inverse">
                {t('brand.name')}
              </Brand>
              <Typography as="p" role="body/sm" className="footer__tagline">
                {t('footer.tagline')}
              </Typography>
            </Stack>
            {COLUMNS.map((column) => (
              <Stack key={column.titleKey} gap={3} as="nav">
                <Typography as="h2" role="meta/caps" className="footer__heading">
                  {t(column.titleKey)}
                </Typography>
                {(column.titleKey === 'footer.game' && onlyGame
                  ? [
                      column.links.find((link) => link.labelKey === 'footer.myGames')!,
                      ...column.links.filter((link) => link.labelKey !== 'footer.myGames'),
                    ]
                  : column.links
                ).map((link) => (
                  <Link
                    key={link.labelKey}
                    href={link.href === '/premium' ? premiumInfoHref : link.labelKey === 'footer.myGames' && onlyGame ? premiumGameUrl(onlyGame) : link.href}
                    testId={link.href === '/premium' ? 'footer-premium' : link.labelKey === 'footer.contact' ? 'footer-contact' : undefined}
                    onClick={link.href ? undefined : () => openSupportDialog()}
                  >
                    {link.href === '/premium' && <CrownIcon />}{link.labelKey === 'footer.myGames' && onlyGame ? t('footer.gameName', { name: onlyGame.name }) : t(link.labelKey)}
                  </Link>
                ))}
              </Stack>
            ))}
          </div>
          <Bar align="center" className="footer__legal-rule">
            <Typography as="p" role="meta/legal" className="footer__legal">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </Typography>
            <Cluster gap={4} align="center">
              <Link href={mainSiteUrl('/terms')} variant="quiet" target="_self" testId="footer-terms">
                {t('footer.terms')}
              </Link>
              <Link href="/policy" variant="quiet" target="_self">
                {t('footer.privacy')}
              </Link>
              {!isDotCom() && (
                <Link variant="quiet" onClick={openCookieSettings} testId="footer-cookies">
                  {t('cookies.settings')}
                </Link>
              )}
              {!isDotCom() && (
                <Link href="https://santa-secret.com" variant="quiet" testId="footer-global">
                  English version
                </Link>
              )}
              <ThemeToggle
                iconOnly
                lightLabel={t('theme.light')}
                darkLabel={t('theme.dark')}
                testId="footer-theme"
              />
              <LocaleToggle />
            </Cluster>
          </Bar>
        </Stack>
      </Container>
    </footer>
  );
}
